/**
 * useCollisions — Hook réutilisable qui transforme un mouvement organique
 * de nœuds (souffle) en accélérateur de sérendipité doctrinale.
 *
 * Principe :
 *   • On observe les positions de N nœuds à chaque frame de leur mouvement
 *   • Quand deux nœuds passent en dessous d'un seuil de distance pendant
 *     plus de N secondes (collision tenue), on la marque comme candidate
 *   • À concurrence de MAX_ANALYSES par session, chaque collision candidate
 *     est envoyée au backend qui appelle l'IA doctrinale
 *   • Le résultat est stocké et exposé via `collisions` ; l'UI affiche un
 *     badge sur les deux entités concernées
 *
 * Quota-conscious :
 *   • MAX_ANALYSES = 5 par session (au-delà, incitation premium)
 *   • Débounce 3 s minimum entre deux analyses
 *   • Cache 24h côté backend par paire
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const MAX_ANALYSES_PER_SESSION = 5;
const HOLD_TIME_MS = 1200;       // temps de collision tenue avant analyse
const DEBOUNCE_MS = 3000;        // délai minimum entre deux analyses
const DISTANCE_THRESHOLD = 90;   // px : seuil de collision (à adapter par surface)

export function useCollisions({ active, surface, getEntities, getPosition }) {
  const [collisions, setCollisions] = useState({});     // pair_key → analysis
  const [analysisCount, setAnalysisCount] = useState(0);
  const [busyPair, setBusyPair] = useState(null);
  const [quotaExceeded, setQuotaExceeded] = useState(false);

  const holdRef = useRef({});          // pair_key → start_ts
  const lastAnalysisRef = useRef(0);   // timestamp dernière analyse
  const analyzedRef = useRef(new Set()); // paires déjà analysées cette session

  useEffect(() => {
    if (!active) {
      holdRef.current = {};
      return undefined;
    }
    const interval = setInterval(() => {
      const entities = getEntities();
      if (!entities || entities.length < 2) return;
      const now = performance.now();

      for (let i = 0; i < entities.length; i++) {
        for (let j = i + 1; j < entities.length; j++) {
          const a = entities[i];
          const b = entities[j];
          const pa = getPosition(a);
          const pb = getPosition(b);
          if (!pa || !pb) continue;
          const dx = pa.x - pb.x;
          const dy = pa.y - pb.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const key = [a.id, b.id].sort().join("|");

          if (dist < DISTANCE_THRESHOLD) {
            if (!holdRef.current[key]) holdRef.current[key] = now;
            const heldFor = now - holdRef.current[key];
            const alreadyAnalyzed = analyzedRef.current.has(key);
            const canAnalyze = heldFor >= HOLD_TIME_MS
              && !alreadyAnalyzed
              && analysisCount < MAX_ANALYSES_PER_SESSION
              && (now - lastAnalysisRef.current) >= DEBOUNCE_MS
              && !busyPair;
            if (canAnalyze) {
              lastAnalysisRef.current = now;
              analyzedRef.current.add(key);
              setBusyPair(key);
              (async () => {
                try {
                  const r = await api.post("/workspace/collision/analyze", {
                    entity_a: { id: a.id, name: a.name, kind: a.kind, role: a.role },
                    entity_b: { id: b.id, name: b.name, kind: b.kind, role: b.role },
                    surface: surface || "unknown",
                  });
                  const d = r.data;
                  setCollisions((prev) => ({ ...prev, [key]: d }));
                  setAnalysisCount((c) => {
                    const next = c + 1;
                    if (next >= MAX_ANALYSES_PER_SESSION) setQuotaExceeded(true);
                    return next;
                  });
                } catch {
                  // fallback silencieux — on n'affiche pas d'erreur agressive
                  // pour éviter de casser l'ambiance du mode Collision
                } finally {
                  setBusyPair(null);
                }
              })();
            }
          } else {
            if (holdRef.current[key]) delete holdRef.current[key];
          }
        }
      }
    }, 250);
    return () => clearInterval(interval);
  }, [active, getEntities, getPosition, analysisCount, busyPair, surface]);

  // Retourne les collisions par entity id (pour affichage badge sur chaque nœud)
  const collisionsByEntity = useCallback(() => {
    const idx = {};
    Object.entries(collisions).forEach(([key, data]) => {
      if (!data || data.kind === "none") return;
      const [aid, bid] = key.split("|");
      idx[aid] = idx[aid] || [];
      idx[bid] = idx[bid] || [];
      idx[aid].push(data);
      idx[bid].push(data);
    });
    return idx;
  }, [collisions]);

  const resetSession = useCallback(() => {
    analyzedRef.current.clear();
    holdRef.current = {};
    setCollisions({});
    setAnalysisCount(0);
    setQuotaExceeded(false);
  }, []);

  return {
    collisions,
    collisionsByEntity: collisionsByEntity(),
    analysisCount,
    busyPair,
    quotaExceeded,
    maxAnalyses: MAX_ANALYSES_PER_SESSION,
    resetSession,
  };
}
